import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useUser } from "../UserContext";          // ← import your user context
import { FaBook, FaPaperPlane } from "react-icons/fa";
import StudentSelection from "./StudentSelection";

const CaseScenario = () => {
  const navigate = useNavigate();
  const { user } = useUser();                       // ← get the logged‐in user
  const [modules, setModules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      criteria: "",
    },
  });

  // Fetch all modules on mount
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/topics`,
          { withCredentials: true }
        );
        setModules(response.data);
      } catch (err) {
        console.error("Error fetching modules:", err);
      }
    };
    fetchModules();
  }, []);

  // Fetch courses when module changes
  useEffect(() => {
    const fetchCourses = async () => {
      if (!selectedModule) {
        setCourses([]);
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/courses/${selectedModule}`,
          { withCredentials: true }
        );
        setCourses(response.data);
      } catch (err) {
        console.error("Error fetching courses:", err);
      }
    };
    fetchCourses();
  }, [selectedModule]);

  const handleModuleChange = (e) => {
    setSelectedModule(e.target.value);
    setSelectedCourse("");
  };

  const handleCourseChange = (e) => {
    setSelectedCourse(e.target.value);
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const onSubmit = async (data) => {
    try {
      if (!selectedModule || !selectedCourse) {
        return alert("Please select both module and course");
      }

      // If user is not loaded yet, block
      if (!user || !user.name) {
        return alert("Unable to identify teacher username.");
      }

      // Build payload, now including teacherUsername
      const payload = {
        moduleId: selectedModule,
        courseIds: [selectedCourse],
        studentIds: selectedStudents,
        title: data.title,
        description: data.description,
        criteria: data.criteria,
        teacherUsername: user.name,             // ← pass current teacher's name
      };

      console.log("Complete assignment data:", payload);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/assignments/case`,
        payload,
        { withCredentials: true }
      );

      alert("Assignment published successfully!");

      // Reset form + state
      reset();
      setSelectedModule("");
      setSelectedCourse("");
      setCourses([]);
      setSelectedStudents([]);

      // Navigate back to dashboard (optional)
      navigate("/teacher-dashboard/home", { state: { moduleData: payload } });
    } catch (err) {
      console.error("Error publishing assignment:", err);
      alert("Failed to publish assignment. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
          <h2 className="text-3xl font-bold text-white text-center flex items-center justify-center">
            <FaBook className="mr-3" />
            Case Scenarios
          </h2>
        </div>

        {/* Module + Course Picker */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Module
              </label>
              <select
                value={selectedModule}
                onChange={handleModuleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a module</option>
                {modules.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Course
              </label>
              <select
                value={selectedCourse}
                onChange={handleCourseChange}
                disabled={!selectedModule}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select a course</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Assignment Form */}
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignment Title
              </label>
              <input
                type="text"
                {...register("title", { required: "Title is required" })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter assignment title"
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                {...register("description", { required: "Description is required" })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="4"
                placeholder="Enter assignment description"
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Evaluation Criteria
              </label>
              <textarea
                {...register("criteria", { required: "Criteria is required" })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="4"
                placeholder="Enter evaluation criteria"
              />
              {errors.criteria && (
                <p className="text-red-500 text-sm mt-1">{errors.criteria.message}</p>
              )}
            </div>

            {/* Student Selection */}
            <StudentSelection
              selectedStudents={selectedStudents}
              onStudentSelect={handleStudentSelect}
            />

            {/* Submit */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting || selectedStudents.length === 0}
                className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  isSubmitting || selectedStudents.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
                }`}
              >
                <FaPaperPlane className="mr-2" />
                Create Assignment
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CaseScenario;
